import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import {
  Box,
  Button,
  Grid,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  Paper,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import {
  CriarEntradaDeNotasCompraFormData,
  criarEntradaDeNotasCompraSchema,
} from "./schema/criar-entrada-de-notas-compras.schema";
import { SupplierTypeEnum } from "../../shared/utils/enum/enum";
import { useMutation } from "@tanstack/react-query";
import { toastError } from "../../shared/services/errors/erros-handler";
import { entradaDeNotasCompraService } from "./service/entrada-de-notas-compra.service";
import { SelectCliente } from "../../shared/components/formulario/input-select-cliente/selectCliente";
import { SelectObra } from "../../shared/components/formulario/input-select-obra/selectObra";
import InputDescricao from "../../shared/components/formulario/input-descricao/inputDescricao";
import { SelectFornecedores } from "../../shared/components/formulario/input-select-fornecedores/selectFornecedores";
import InputData from "../../shared/components/formulario/input-data/inputData";
import { PessoaJuridicaFormPart } from "../../shared/components/formulario/parts/PessoaJuridicaFormPart/pessoa-juridica.part";
import { PessoaFisicaFormPart } from "../../shared/components/formulario/parts/PessoaFisicaFormPart/pessoa-fisica.part";
import { EnderecoFormPart } from "../../shared/components/formulario/parts/EnderecoFormPart/enderecoform.part";
import { ICnpjData } from "../../shared/components/buscarCNPJ/buscar-cnpj";
import InputMascarado from "../../shared/components/formulario/input-mascarado/InputMascarado";
import { mascaraMoedaReal } from "../../shared/utils/mascaras/mascaraMoedaReal";

export function AdicionarNotaDeCompra() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);

  const methods = useForm<CriarEntradaDeNotasCompraFormData>({
    resolver: yupResolver(criarEntradaDeNotasCompraSchema),
    defaultValues: {
      newRegister: false,
      isClient: true,
      items: [],
    },
  });

  const {
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = methods;

  const newRegister = watch("newRegister");
  const personType = watch("personType");
  const isClient = watch("isClient");
  const idCliente = watch("clientId");

  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));

  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });

  const preencherCampos = (dados: ICnpjData) => {
    setValue(
      "corporateName" as keyof CriarEntradaDeNotasCompraFormData,
      dados.razaoSocial || ""
    );
    setValue(
      "fantasyName" as keyof CriarEntradaDeNotasCompraFormData,
      dados.fantasia || dados.razaoSocial || ""
    );
  };

  const { isPending, mutate } = useMutation({
    mutationFn: async (data: CriarEntradaDeNotasCompraFormData) => {
      const result = await entradaDeNotasCompraService.adicionar(data);
      return result;
    },
    onSuccess: () => {
      toast.success("Nota fiscal criada com sucesso!");
      navigate("/financeiro/entrada-notas-compra");
    },
    onError: (err: any) => {
      toastError(err);
    },
  });

  return (
    <BaseLayout title="Adicionar Nota de Compra">
      <FerramanetasDetalhe
        mostraBotaoSalvarEVoltar={false}
        mostraBotaoApagar={false}
        mostraBotaoNovo={false}
        mostraBotaoVoltar
        aoClicarBotaoSalvar={handleSubmit((formData) => mutate(formData))}
        aoClicarBotaoVoltar={() =>
          navigate("/financeiro/entrada-notas-compras")
        }
      />
      <Paper sx={{ p: 2 }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="Dados da Nota e Fornecedor" />
          <Tab label="Frete e Transporte" />
          <Tab label="Itens Comprados" />
        </Tabs>

        {/* Aba 0: Dados da Nota e Fornecedor */}
        {tab === 0 && (
          <Box>
            <Grid container spacing={2}>
              {/* newRegister */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="newRegister"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Cadastrar novo fornecedor?"
                      fullWidth
                      size="small"
                      value={field.value ? "true" : "false"}
                      onChange={(e) =>
                        field.onChange(e.target.value === "true")
                      }
                    >
                      <MenuItem value="false">Não</MenuItem>
                      <MenuItem value="true">Sim</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              {/* isClient */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="isClient"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      select
                      label="Esta nota é relacionada a um cliente?"
                      fullWidth
                      size="small"
                      value={field.value ? "true" : "false"}
                      onChange={(e) =>
                        field.onChange(e.target.value === "true")
                      }
                    >
                      <MenuItem value="true">Sim</MenuItem>
                      <MenuItem value="false">Não</MenuItem>
                    </TextField>
                  )}
                />
              </Grid>
              {/* Cliente e Obra */}
              {isClient && (
                <>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="clientId"
                      control={control}
                      render={({ field }) => (
                        <SelectCliente
                          value={field.value || ""}
                          onChange={field.onChange}
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="workId"
                      control={control}
                      render={({ field }) => (
                        <SelectObra
                          value={field.value || ""}
                          onChange={field.onChange}
                          idCliente={idCliente || ""}
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
              {/* Fornecedor existente */}
              {!newRegister && (
                <Grid item xs={12} md={6}>
                  <Controller
                    name="supplierId"
                    control={control}
                    render={({ field }) => (
                      <SelectFornecedores
                        value={field.value || ""}
                        onChange={field.onChange}
                      />
                    )}
                  />
                </Grid>
              )}
              {/* Novo fornecedor */}
              {newRegister && (
                <>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="personType"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          select
                          label="Tipo de Pessoa"
                          error={!!errors.personType}
                          helperText={errors.personType?.message}
                          fullWidth
                          size="small"
                        >
                          <MenuItem value={SupplierTypeEnum.PJ}>PJ</MenuItem>
                          <MenuItem value={SupplierTypeEnum.PF}>PF</MenuItem>
                        </TextField>
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={12}>
                    {personType === SupplierTypeEnum.PJ ? (
                      <PessoaJuridicaFormPart
                        methods={methods}
                        preencherCampos={preencherCampos}
                      />
                    ) : (
                      <PessoaFisicaFormPart methods={methods} mdDown={mdDown} />
                    )}
                  </Grid>
                  <Grid item xs={12} md={12}>
                    <EnderecoFormPart methods={methods} mdDown={mdDown} />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="stateRegistration"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Inscrição Estadual"
                          error={!!errors.stateRegistration}
                          helperText={errors.stateRegistration?.message}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name="municipalRegistration"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Inscrição Municipal"
                          error={!!errors.municipalRegistration}
                          helperText={errors.municipalRegistration?.message}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                </>
              )}
              {/* Campos da nota fiscal */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="invoiceNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Número da Nota"
                      error={!!errors.invoiceNumber}
                      helperText={errors.invoiceNumber?.message}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="series"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Série"
                      error={!!errors.series}
                      helperText={errors.series?.message}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="sheet"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Folha"
                      error={!!errors.sheet}
                      helperText={errors.sheet?.message}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputData
                  name="issueDate"
                  methods={methods}
                  label="Data de Emissão"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputData
                  name="entryDate"
                  methods={methods}
                  label="Data de Entrada"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputData
                  name="exitDate"
                  methods={methods}
                  label="Data de Saída"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="exitTime"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Hora de Saída"
                      error={!!errors.exitTime}
                      helperText={errors.exitTime?.message}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="accessKey"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Chave de Acesso"
                      error={!!errors.accessKey}
                      helperText={errors.accessKey?.message}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="authorizationProtocol"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Protocolo de Autorização"
                      error={!!errors.authorizationProtocol}
                      helperText={errors.authorizationProtocol?.message}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="operationNature"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Natureza da Operação"
                      error={!!errors.operationNature}
                      helperText={errors.operationNature?.message}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="operationType"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Tipo de Operação"
                      error={!!errors.operationType}
                      helperText={errors.operationType?.message}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="cfop"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="CFOP"
                      error={!!errors.cfop}
                      helperText={errors.cfop?.message}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <InputDescricao
                  name="notes"
                  methods={methods}
                  label="Observações"
                />
              </Grid>
              {/* Valores */}
              <Grid item xs={12} md={4}>
                <InputMascarado
                  name="totalValue"
                  methods={methods}
                  label="Valor Total"
                  mask={mascaraMoedaReal}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputMascarado
                  name="productsValue"
                  methods={methods}
                  label="Valor dos Produtos"
                  mask={mascaraMoedaReal}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputMascarado
                  name="discountValue"
                  methods={methods}
                  label="Valor do Desconto"
                  mask={mascaraMoedaReal}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputMascarado
                  name="freightValue"
                  methods={methods}
                  label="Valor do Frete"
                  mask={mascaraMoedaReal}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputMascarado
                  name="insuranceValue"
                  methods={methods}
                  label="Valor do Seguro"
                  mask={mascaraMoedaReal}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="otherExpensesValue"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Outras Despesas"
                      type="number"
                      error={!!errors.otherExpensesValue}
                      helperText={errors.otherExpensesValue?.message}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputMascarado
                  name="taxesValue"
                  methods={methods}
                  label="Valor dos Impostos"
                  mask={mascaraMoedaReal}
                />
              </Grid>
              {/* Duplicatas */}
              <Grid item xs={12} md={4}>
                <Controller
                  name="duplicateNumber"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Número da Duplicata"
                      error={!!errors.duplicateNumber}
                      helperText={errors.duplicateNumber?.message}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <InputData
                  name="duplicateDueDate"
                  methods={methods}
                  label="Vencimento da Duplicata"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="duplicateValue"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Valor da Duplicata"
                      type="number"
                      error={!!errors.duplicateValue}
                      helperText={errors.duplicateValue?.message}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Box mt={2} display="flex" justifyContent="flex-end">
              <Button variant="contained" onClick={() => setTab(1)}>
                Próximo: Frete e Transporte
              </Button>
            </Box>
          </Box>
        )}

        {/* Aba 1: Frete e Transporte */}
        {tab === 1 && (
          <Box>
            <Typography variant="h6" gutterBottom paddingBottom={3}>
              Frete e Transporte
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <Controller
                  name="freightResponsibility"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Frete por Conta"
                      error={!!errors.freightResponsibility}
                      helperText={errors.freightResponsibility?.message}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="volumesQuantity"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Quantidade de Volumes"
                      type="number"
                      error={!!errors.volumesQuantity}
                      helperText={errors.volumesQuantity?.message}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="grossWeight"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Peso Bruto"
                      type="number"
                      error={!!errors.grossWeight}
                      helperText={errors.grossWeight?.message}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Controller
                  name="netWeight"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Peso Líquido"
                      type="number"
                      error={!!errors.netWeight}
                      helperText={errors.netWeight?.message}
                      fullWidth
                      size="small"
                    />
                  )}
                />
              </Grid>
            </Grid>
            <Box mt={2} display="flex" justifyContent="space-between">
              <Button variant="outlined" onClick={() => setTab(0)}>
                Voltar
              </Button>
              <Button variant="contained" onClick={() => setTab(2)}>
                Próximo: Itens Comprados
              </Button>
            </Box>
          </Box>
        )}

        {/* Aba 2: Itens Comprados */}
        {tab === 2 && (
          <Box>
            <Typography variant="h6" gutterBottom paddingBottom={3}>
              Itens Comprados
            </Typography>
            <Grid container spacing={2}>
              {fields.map((item, idx) => (
                <Grid
                  container
                  spacing={2}
                  key={item.id}
                  sx={{
                    mb: 2,
                    p: 2,
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                  }}
                >
                  <Grid item xs={12} md={6}>
                    <Controller
                      name={`items.${idx}.productCode`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Código do Produto"
                          error={!!errors.items?.[idx]?.productCode}
                          helperText={errors.items?.[idx]?.productCode?.message}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Controller
                      name={`items.${idx}.productDescription`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Descrição do Produto"
                          error={!!errors.items?.[idx]?.productDescription}
                          helperText={
                            errors.items?.[idx]?.productDescription?.message
                          }
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name={`items.${idx}.quantity`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Quantidade"
                          type="number"
                          error={!!errors.items?.[idx]?.quantity}
                          helperText={errors.items?.[idx]?.quantity?.message}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name={`items.${idx}.unitPrice`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Preço Unitário"
                          type="number"
                          error={!!errors.items?.[idx]?.unitPrice}
                          helperText={errors.items?.[idx]?.unitPrice?.message}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <InputMascarado
                      name={`items.${idx}.totalPrice`}
                      methods={methods}
                      label="Preço Total"
                      mask={mascaraMoedaReal}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name={`items.${idx}.unit`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="Unidade"
                          error={!!errors.items?.[idx]?.unit}
                          helperText={errors.items?.[idx]?.unit?.message}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name={`items.${idx}.ncm`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="NCM"
                          error={!!errors.items?.[idx]?.ncm}
                          helperText={errors.items?.[idx]?.ncm?.message}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name={`items.${idx}.cfop`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="CFOP"
                          error={!!errors.items?.[idx]?.cfop}
                          helperText={errors.items?.[idx]?.cfop?.message}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name={`items.${idx}.ean`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="EAN"
                          error={!!errors.items?.[idx]?.ean}
                          helperText={errors.items?.[idx]?.ean?.message}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name={`items.${idx}.cst`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="CST"
                          error={!!errors.items?.[idx]?.cst}
                          helperText={errors.items?.[idx]?.cst?.message}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name={`items.${idx}.ipi`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="IPI (%)"
                          type="number"
                          error={!!errors.items?.[idx]?.ipi}
                          helperText={errors.items?.[idx]?.ipi?.message}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name={`items.${idx}.icms`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="ICMS (%)"
                          type="number"
                          error={!!errors.items?.[idx]?.icms}
                          helperText={errors.items?.[idx]?.icms?.message}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name={`items.${idx}.pis`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="PIS (%)"
                          type="number"
                          error={!!errors.items?.[idx]?.pis}
                          helperText={errors.items?.[idx]?.pis?.message}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Controller
                      name={`items.${idx}.cofins`}
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          label="COFINS (%)"
                          type="number"
                          error={!!errors.items?.[idx]?.cofins}
                          helperText={errors.items?.[idx]?.cofins?.message}
                          fullWidth
                          size="small"
                        />
                      )}
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={12}
                    display="flex"
                    justifyContent="flex-end"
                  >
                    <Button
                      color="error"
                      variant="outlined"
                      onClick={() => remove(idx)}
                      size="small"
                    >
                      Remover Item
                    </Button>
                  </Grid>
                </Grid>
              ))}

              {fields.length === 0 && (
                <Grid item xs={12}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    align="center"
                    sx={{ py: 4 }}
                  >
                    Nenhum item adicionado. Clique em "Adicionar Item" para
                    começar.
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Box mt={2} display="flex" justifyContent="space-between">
              <Button variant="outlined" onClick={() => setTab(1)}>
                Voltar
              </Button>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() =>
                    append({
                      productCode: "",
                      productDescription: "",
                      quantity: 1,
                      unitPrice: 0,
                      totalPrice: 0,
                      ncm: "",
                      cfop: "",
                      unit: "",
                      ean: "",
                      cst: "",
                      ipi: 0,
                      icms: 0,
                      pis: 0,
                      cofins: 0,
                    })
                  }
                >
                  Adicionar Item
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={handleSubmit((formData) => mutate(formData))}
                  disabled={isPending || fields.length === 0}
                >
                  Salvar Nota de Compra
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Paper>
    </BaseLayout>
  );
}
